import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useAccount, usePublicClient } from 'wagmi';
import { formatEther, formatUnits } from 'viem';
import { bullvsbear } from '../../../.config';
import BullvsBearABI from '../../../abi/bullvsbearABI.json';


/* global BigInt */
function formatPrice(value, decimals = 8) {
  if (!value || value === 0n) return '0.00';
  // Use BigInt division for accuracy, then format
  const divisor = 10 ** decimals;
  const price = Number(value) / divisor;
  return price.toFixed(2);
}

const initialState = {
  status: 'idle',
  currentEpoch: 0,
  rounds: {},
  history: [],
  isHistoryPaneOpen: false,
  isChartPaneOpen: false,
  lastOraclePrice: '0',
  hasEnteredUp: false,
  hasEnteredDown: false,
  // chain clock minus the local clock, so countdowns follow the contract's timestamps
  chainOffsetMs: 0,
  // fee total and early-bird rules, read once
  config: { feeBps: 1000, earlyBirdCount: 4, earlyBirdBonusBps: 2000, minBet: 0, intervalSeconds: 300 },
  // per epoch: house seed per side, players' deposits, early birds used, refunded
  roundExtras: {},
};

// Map the round tuple (array) from contract to an object with named fields
function mapRoundTuple(data) {
  if (!data || !Array.isArray(data)) return null;
  /*
0	epoch	uint256	
1	startTimestamp	uint256	
2	lockTimestamp	uint256	
3	closeTimestamp	uint256	
4	lockPrice	int256	
5	closePrice	int256	
6	lockOracleId	uint256	
7	closeOracleId	uint256	
8	totalAmount	uint256	
9	bullAmount	uint256	
10	bearAmount	uint256	
11	rewardBaseCalAmount	uint256	
12	rewardAmount	uint256	
13	oracleCalled	bool
  */

  /* new STRUCT
      struct Round {
          uint256 epoch;
          uint256 startTimestamp;
          uint256 lockTimestamp;
          uint256 closeTimestamp;
          uint256 executedTimestamp; 
          int256 lockPrice;
          int256 closePrice;
          uint256 totalAmount;
          uint256 bullAmount;
          uint256 bearAmount;
          uint256 rewardBaseCalAmount;
          uint256 rewardAmount;
          bool oracleCalled;
      }
  */

  return {
    epoch: data[0],
    startTimestamp: data[1],
    lockTimestamp: data[2],
    closeTimestamp: data[3],
    executedTimestamp: data[4],
    lockPrice: formatPrice(data[5]),      // formatted as string with 2 decimals
    closePrice: formatPrice(data[6]),     // formatted as string with 2 decimals
    totalAmount: data[7],
    bullAmount: data[8],
    bearAmount: data[9],
    rewardBaseCalAmount: data[10],
    rewardAmount: data[11],
    oracleCalled: data[12]
  };
}

function predictionsReducer(state, action) {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_CURRENT_EPOCH':
      return { ...state, currentEpoch: action.payload };
    case 'UPDATE_ROUND':
      return {
        ...state,
        rounds: {
          ...state.rounds,
          [action.payload.epoch]: {
            ...state.rounds[action.payload.epoch],
            ...action.payload.data,
          },
        },
      };
    case 'SET_ROUNDS':
      return { ...state, rounds: action.payload };
    case 'SET_HISTORY':
      return { ...state, history: action.payload };
    case 'SET_CHART_PANE':
      return { ...state, isChartPaneOpen: action.payload };
    case 'SET_HISTORY_PANE':
      return { ...state, isHistoryPaneOpen: action.payload };
    case 'SET_ORACLE_PRICE':
      return { ...state, lastOraclePrice: action.payload };
    case 'SET_CHAIN_OFFSET':
      return { ...state, chainOffsetMs: action.payload };
    case 'SET_CONFIG':
      return { ...state, config: { ...state.config, ...action.payload } };
    case 'SET_ROUND_EXTRAS':
      return { ...state, roundExtras: { ...state.roundExtras, ...action.payload } };
    case 'SET_ENTERED_UP':
      return { ...state, hasEnteredUp: action.payload };
    case 'SET_ENTERED_DOWN':
      return { ...state, hasEnteredDown: action.payload };
    case 'UPDATE_CLAIMED_STATUS':
      // Atualiza o status de claim para rodadas específicas
      return {
        ...state,
        rounds: Object.keys(state.rounds).reduce((acc, key) => {
          const round = state.rounds[key];
          if (action.payload.epochs.includes(key.toString())) {
            // Atualiza o round com o novo status de claim
            acc[key] = {
              ...round,
              userBetInfo: round.userBetInfo ? {
                ...round.userBetInfo,
                claimed: action.payload.claimed
              } : null
            };
          } else {
            acc[key] = round;
          }
          return acc;
        }, {}),
        history: state.history.map(entry => {
          if (action.payload.epochs.includes(entry.epoch.toString())) {
            return { ...entry, claimed: action.payload.claimed };
          }
          return entry;
        })
      };

    case 'REFRESH_ROUND_DATA':
      return {
        ...state,
        rounds: Object.keys(state.rounds).reduce((acc, key) => {
          const round = state.rounds[key];
          if (round.epoch === action.payload.epoch) {
            acc[key] = { ...round, ...action.payload.data };
          } else {
            acc[key] = round;
          }
          return acc;
        }, {})
      };
    default:
      return state;
  }
}

const PredictionsContext = createContext();

export function PredictionsProvider({ children }) {
  const [state, dispatch] = useReducer(predictionsReducer, initialState);
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const setIsChartPaneOpen = (open) => {
    dispatch({ type: 'SET_CHART_PANE', payload: open });
  };

 // console.log('PredictionsProvider initialized with address:', window.location.pathname);
  const isOnPredictionsPage = typeof window !== 'undefined' && 
    (window.location.pathname.includes('/prediction') || 
     window.location.pathname.includes('/predictions') ||
     window.location.hash.includes('/prediction') ||
     window.location.hash.includes('/predictions'));
  const readSafe = useCallback(
    (functionName, args = []) =>
      publicClient
        .readContract({ address: bullvsbear, abi: BullvsBearABI, functionName, args })
        .catch(() => undefined),
    [publicClient]
  );

  // fee total and early-bird rules (constant per deploy, changed only by governance)
  const fetchConfig = useCallback(async () => {
    const names = ['NFT_NODE_FEE', 'REFERRAL_FEE', 'MIN_AMOUNT_FEE', 'STAKE_FEE', 'CYNTHS_FEE', 'HOUSE_FEE'];
    const [fees, earlyBirdCount, earlyBirdBonus, minBet, intervalSeconds] = await Promise.all([
      Promise.all(names.map((n) => readSafe(n))),
      readSafe('EARLY_BIRD_COUNT'),
      readSafe('EARLY_BIRD_BONUS'),
      readSafe('minBetAmount'),
      readSafe('intervalSeconds'),
    ]);
    const payload = {};
    if (fees.every((f) => f !== undefined)) payload.feeBps = fees.reduce((sum, f) => sum + Number(f), 0);
    if (fees[1] !== undefined) payload.referralBps = Number(fees[1]);
    if (earlyBirdCount !== undefined) payload.earlyBirdCount = Number(earlyBirdCount);
    if (earlyBirdBonus !== undefined) payload.earlyBirdBonusBps = Number(earlyBirdBonus);
    if (minBet !== undefined) payload.minBet = Number(formatUnits(minBet, 6));
    if (intervalSeconds !== undefined) payload.intervalSeconds = Number(intervalSeconds);
    dispatch({ type: 'SET_CONFIG', payload });
  }, [readSafe]);

  // House seed, deposits, early birds and refund flag of the rounds on screen. Settled rounds never change, so
  // they are read once.
  const settledExtras = React.useRef({});
  const fetchRoundExtras = useCallback(async (roundsData) => {
    const entries = await Promise.all(
      Object.entries(roundsData).map(async ([epoch, round]) => {
        if (settledExtras.current[epoch]) return [epoch, settledExtras.current[epoch]];
        const e = BigInt(epoch);
        const [houseBullAmount, houseBearAmount, userBullDeposit, userBearDeposit, earlyBirds, refunded] = await Promise.all([
          readSafe('houseBullAmount', [e]),
          readSafe('houseBearAmount', [e]),
          readSafe('userBullDeposit', [e]),
          readSafe('userBearDeposit', [e]),
          round.status === 'next' ? readSafe('earlyBirdCount', [e]) : undefined,
          round.oracleCalled ? readSafe('refunded', [e]) : undefined,
        ]);
        const extras = { houseBullAmount, houseBearAmount, userBullDeposit, userBearDeposit, refunded: !!refunded };
        if (earlyBirds !== undefined) extras.earlyBirds = Number(earlyBirds);
        if (round.oracleCalled) settledExtras.current[epoch] = extras;
        return [epoch, extras];
      })
    );
    dispatch({ type: 'SET_ROUND_EXTRAS', payload: Object.fromEntries(entries) });
  }, [readSafe]);

  // Fetch multiple rounds around the current epoch
  const fetchMultipleRounds = useCallback(async (currentEpoch) => {
    try {
      const roundPromises = [];
      // two upcoming epochs, the next, the live one and the last 6 finished rounds (results strip)
      for (let i = -2; i <= 7; i++) {
        const epoch = currentEpoch - i;
        // epoch 0 never exists (rounds start at 1)
        if (epoch >= 1) {
          roundPromises.push(
            publicClient.readContract({
              address: bullvsbear,
              abi: BullvsBearABI,
              functionName: 'rounds',
              args: [epoch]
            }).then(data => ({ epoch, data: mapRoundTuple(data, epoch) }))
              .catch(error => {
                console.log(`Round ${epoch} not available:`, error.message);
                return { epoch, data: null };
              })
          );
        }
      }

      const rounds = await Promise.all(roundPromises);
      const roundsData = {};

      rounds.forEach(({ epoch, data }) => {
        // past epochs that never started (e.g. before the genesis) are not rounds
        if (data && (epoch >= currentEpoch || Number(data.startTimestamp) > 0)) {
          let status = 'expired';
          if (epoch === currentEpoch - 1) status = 'live';
          else if (epoch === currentEpoch) status = 'next';
          else if (epoch > currentEpoch) status = 'later';
          else if (epoch < currentEpoch - 1) status = 'expired';

          roundsData[epoch] = {
            ...data,
            status,
          };
        }
      });

      dispatch({ type: 'SET_ROUNDS', payload: roundsData });
      // house seed, deposits and refunds fill in right after; the rounds show without waiting for them
      fetchRoundExtras(roundsData);
    } catch (error) {
      console.error('Error fetching multiple rounds:', error);
    }
  }, [publicClient, fetchRoundExtras]);

  const fetchContractData = useCallback(async () => {
    try {
      if (!publicClient) throw new Error("Public client not initialized");

      const currentEpoch = await publicClient.readContract({
        address: bullvsbear,
        abi: BullvsBearABI,
        functionName: 'currentEpoch',
      });

      const currentEpochNumber = Number(currentEpoch);

      const block = await publicClient.getBlock();
      dispatch({ type: 'SET_CHAIN_OFFSET', payload: Number(block.timestamp) * 1000 - Date.now() });

      // Fetch and map the current round
      const roundData = await publicClient.readContract({
        address: bullvsbear,
        abi: BullvsBearABI,
        functionName: 'rounds',
        args: [currentEpoch]
      });

      const mappedRound = mapRoundTuple(roundData, currentEpochNumber);

      dispatch({ type: 'SET_CURRENT_EPOCH', payload: currentEpochNumber });

      // Save current round data
      if (mappedRound) {
        dispatch({
          type: 'UPDATE_ROUND',
          payload: {
            epoch: currentEpochNumber,
            data: {
              ...mappedRound,
              status: 'next'
            }
          }
        });
      }

      const oracleData = await publicClient.readContract({
        address: bullvsbear,
        abi: BullvsBearABI,
        functionName: 'getPriceFromOracle'
      });

      // Update oracle price: Use the already formatted price string
      const formattedPrice = formatPrice(oracleData[1]);
      dispatch({ type: 'SET_ORACLE_PRICE', payload: formattedPrice });


      // Fetch multiple rounds for context
      await fetchMultipleRounds(currentEpochNumber);

      return true;
    } catch (error) {
      console.error('Error fetching contract data:', error);
      return false;
    }
  }, [publicClient, fetchMultipleRounds]);

  const fetchUserHistory = useCallback(async (userAddress) => {
    try {
      if (!publicClient || !userAddress) return [];

      const userRounds = await publicClient.readContract({
        address: bullvsbear,
        abi: BullvsBearABI,
        functionName: 'getUserRounds',
        args: [userAddress, 0n, 50n]
      });

      if (!userRounds || !userRounds[0]) return [];

      // Prepare history with bet info (filter out empty bets)
      const rawHistory = userRounds[0]
        .map((epoch, index) => {
          const bet = userRounds[1][index];
          console.log(`Processing bet for epoch ${epoch}:`, bet);
          return {
            epoch: Number(epoch),
            position: bet.position ? 'up' : 'down',
            amount: bet.amount,
            claimed: bet.claimed,
          };
        })
        .filter(entry => entry.amount > 0n && entry.epoch !== 0);

      // Fetch round data for each bet (in parallel)
      const roundDataArray = await Promise.all(
        rawHistory.map(async (entry) => {
          try {
            const round = await publicClient.readContract({
              address: bullvsbear,
              abi: BullvsBearABI,
              functionName: 'rounds',
              args: [BigInt(entry.epoch)]
            });
            return round;
          } catch (error) {
            return null;
          }
        })
      );

      // Calculate won/payout per contract logic
      const history = rawHistory.map((entry, i) => {
        const round = roundDataArray[i];
        let won = false;
        let payout = '0';
        if (round && round.oracleCalled) {
          const lockPrice = BigInt(round.lockPrice);
          const closePrice = BigInt(round.closePrice);
          const rewardBaseCalAmount = BigInt(round.rewardBaseCalAmount);
          const rewardAmount = BigInt(round.rewardAmount);
          const userPos = entry.position;
          // Determine win
          if (
            (closePrice > lockPrice && userPos === 'up') ||
            (closePrice < lockPrice && userPos === 'down')
          ) {
            won = true;
            if (rewardBaseCalAmount > 0n) {
              // (betAmount / rewardBaseCalAmount) * rewardAmount, scaled as in contract
              const proportion = (entry.amount * 1000000000000n) / rewardBaseCalAmount;
              const addedReward = (rewardAmount * proportion) / 1000000000000n;
              payout = formatUnits(addedReward, 6);
            }
          }
        }
        return {
          ...entry,
          amount: formatUnits(entry.amount, 6),
          won,
          payout,
        };
      });

      return history;
    } catch (error) {
      console.error('Failed to fetch user history:', error);
      return [];
    }
  }, [publicClient]);

  // Initialize contract data
  useEffect(() => {
    if (!isOnPredictionsPage) {
      console.log('Not on predictions page, skipping initialization');
      return;
    }

    const initializeData = async () => {
      try {
        dispatch({ type: 'SET_STATUS', payload: 'loading' });
        fetchConfig();
        const success = await fetchContractData();

        if (success) {
          dispatch({ type: 'SET_STATUS', payload: 'live' });
        } else {
          dispatch({ type: 'SET_STATUS', payload: 'error' });
        }
      } catch (error) {
        console.error('Failed to initialize predictions:', error);
        dispatch({ type: 'SET_STATUS', payload: 'error' });
      }
    };

    if (publicClient) {
      initializeData();
      const interval = setInterval(fetchContractData, 5000);
      return () => clearInterval(interval);
    }
  }, [fetchContractData, fetchConfig, publicClient, isOnPredictionsPage]);

  // Load user history when address changes
  useEffect(() => {
    if (!isOnPredictionsPage || !address) return;
    const loadUserHistory = async () => {
      if (!address) return;

      try {
        const history = await fetchUserHistory(address);
        dispatch({ type: 'SET_HISTORY', payload: history });
      } catch (error) {
        console.error('Failed to load user history:', error);
      }
    };

    loadUserHistory();
  }, [address, fetchUserHistory, isOnPredictionsPage]);

  const contextValue = {
    state,
    dispatch,
    fetchContractData,
    fetchUserHistory,
    setIsChartPaneOpen,
  };

  return (
    <PredictionsContext.Provider value={contextValue}>
      {children}
    </PredictionsContext.Provider>
  );
}

// Context hooks

export function usePredictions() {
  const context = useContext(PredictionsContext);
  if (!context) {
    throw new Error('usePredictions must be used within a PredictionsProvider');
  }
  return context;
}

export function useGetPredictionsStatus() {
  const { state } = usePredictions();
  return state.status;
}

export function useIsHistoryPaneOpen() {
  const { state } = usePredictions();
  return state.isHistoryPaneOpen;
}

export function useIsChartPaneOpen() {
  const { state } = usePredictions();
  return state.isChartPaneOpen;
}

export function useGetCurrentEpoch() {
  const { state } = usePredictions();
  return state.currentEpoch;
}

export function useGetRounds() {
  const { state } = usePredictions();
  return state.rounds;
}

export function useGetHistory() {
  const { state } = usePredictions();
  return state.history;
}

export function useGetLastOraclePrice() {
  const { state } = usePredictions();
  return state.lastOraclePrice;
}

export function useGetRound(epoch) {
  const { state } = usePredictions();
  return state.rounds[epoch] || null;
}

export function useGetNextRound() {
  const { state } = usePredictions();
  const nextEpoch = state.currentEpoch + 1;
  return state.rounds[nextEpoch] || null;
}

export function useGetRoundWithOffset(offset = 0) {
  const { state } = usePredictions();
  const targetEpoch = state.currentEpoch + offset;
  return state.rounds[targetEpoch] || null;
}

export function useGetConfig() {
  const { state } = usePredictions();
  return state.config;
}

export function useGetRoundExtras(epoch) {
  const { state } = usePredictions();
  return state.roundExtras[epoch] || {};
}

/** Current time on the chain's clock, in ms */
export function useChainOffset() {
  const { state } = usePredictions();
  return state.chainOffsetMs;
}

export function useSetChartPaneOpen() {
  const { setIsChartPaneOpen } = usePredictions();
  return setIsChartPaneOpen;
}